import { useState } from 'react';
import styles from './AddPhoneNumber.module.css';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import clsx from 'clsx';
import { updateTenantPhone } from '../../auth/_requests';
import { motion } from 'framer-motion';
import { formatPhoneNumber } from '../../helpers/utils/utils';
import { isPhoneNumber } from 'class-validator';
import { Button, Tooltip, Whisper } from 'rsuite';
import infoIcon from '../../assets/pages/addProperty/rentalInfo/infoIcon.svg';
import { useAuth } from '../../auth/Auth';

import { X } from 'lucide-react';

const registrationSchema = Yup.object().shape({
  phone: Yup.string()
    .matches(/^\d{10}$/, 'Phone number must be exactly 10 digits')
    .required('Phone number is required')
    .test('isValidPhone', 'Please provide a valid Canadian  phone number', (value) =>
      isPhoneNumber(value || '', 'CA')
    ),
});
interface props {
  setNumber: (number: boolean) => void;
  setVerifyPhone: (number: boolean) => void;
  onClose?: () => void;
}
const AddPhoneNumber = ({ setNumber, setVerifyPhone, onClose }: props) => {
  const [loading, setLoading] = useState(false);
  const { auth, currentUser } = useAuth();
  const [isInValid, setInValid] = useState(false);

  const initialValues = {
    phone: currentUser?.user?.phoneNumber || '',
    phoneExtension: '1',
    acceptTerms: false,
    acceptSMS: false,
  };

  const formik = useFormik({
    initialValues,
    validationSchema: registrationSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setStatus, setSubmitting }) => {
      setLoading(true);
      try {
        await updateTenantPhone(auth?.data.token, {
          userDetails: { phoneNumber: values.phone },
        });
        setNumber(false);
        setVerifyPhone(true);
      } catch (error: unknown) {
        console.error(error);
        const isBlockedPhone =
          error instanceof Error && error.message === 'phone_number_blocked';
        setStatus(
          isBlockedPhone
            ? 'This phone number has been blocked due to suspicious activity. Please contact support@tenantpay.com for assistance.'
            : 'An error occurred while updating the phone number. Please try again later.'
        );
        setSubmitting(false);
      } finally {
        setLoading(false);
      }
    },
  });

  return (
    <motion.main
      className={styles.main}
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className={styles.headingOuter}>
        <span> Add phone number</span> <X className='cursor-pointer' onClick={onClose} />
      </div>

      <div className={styles.formOuter}>
        <form className={styles.form} noValidate onSubmit={formik.handleSubmit}>
          <div className={'inputWrapper'}>
            <div className={styles.tooltipWrap}>
              <label className={'label'}>
                Phone<span className={styles.mandatory}>*</span>
              </label>
              <Whisper
                trigger='hover'
                placement={'right'}
                controlId={`control-id-top`}
                speaker={
                  <Tooltip>
                    <p>
                      Your phone number will be used for Multi-Factor Authentication (MFA) to send
                      verification codes and will also be stored in your profile.
                    </p>
                  </Tooltip>
                }
              >
                <div className={'signUpBtn'}>
                  <Button appearance='subtle' className={styles.btnImg}>
                    <img src={infoIcon} className={styles.infoIcon} alt='info' />
                  </Button>
                </div>
              </Whisper>
            </div>
            <div
              className={clsx(
                formik.touched.phone && (formik.errors.phone || isInValid) ? styles.isInvalid : '',
                formik.touched.phone && !formik.errors.phone && !isInValid ? styles.valid : '',
                `inputField `
              )}
            >
              {formik.values.phone && (
                <div className='text-[16px]/[24px] text-[--text-main]'>+1</div>
              )}
              <input
                type='text'
                autoComplete='off'
                {...formik.getFieldProps('phone')}
                className={`${styles.phoneInput} `}
                placeholder='+1 (XXX) XXX-XXXX'
                maxLength={14}
                value={formatPhoneNumber(formik.values.phone)}
                onChange={(e) => {
                  const formattedPhone = e.target.value.replace(/[^\d]/g, '').slice(0, 10);
                  formik.setFieldValue('phone', formattedPhone);
                  if (formattedPhone.length === 10) {
                    if (!isPhoneNumber(formattedPhone, 'CA')) {
                      setInValid(true);
                      formik.setFieldError('phone', 'Enter a valid Canadian phone number');
                    } else {
                      setInValid(false);
                      formik.setFieldError('phone', '');
                    }
                  } else {
                    setInValid(false);
                  }
                }}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                  }
                }}
              />
            </div>
            {formik.touched.phone && formik.errors.phone && (
              <div className={styles.error}>
                <span>{formik.errors.phone}</span>
              </div>
            )}
          </div>

          <div className='text-center'>
            <button
              type='submit'
              disabled={formik.isSubmitting || !formik.isValid || isInValid || !formik.dirty}
              className={`${styles.submitBtn} ${
                (formik.isSubmitting || !formik.isValid || isInValid || !formik.dirty) &&
                styles.disableBtn
              }`}
            >
              {!loading && <span>Next</span>}
              {loading && (
                <span className='indicator-progress' style={{ display: 'block' }}>
                  Please wait... <span className={styles.spinner}></span>
                </span>
              )}
            </button>
          </div>
        </form>
      </div>
    </motion.main>
  );
};

export default AddPhoneNumber;
